import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormGroup,
  Checkbox,
  Button,
  Alert,
  LinearProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { QuizQuestion } from '../../services/trainingService';

interface QuizEngineProps {
  questions: QuizQuestion[];
  onComplete: (score: number, passed: boolean) => void;
  passingScore?: number;
  title?: string;
}

const QuizEngine: React.FC<QuizEngineProps> = ({
  questions,
  onComplete,
  passingScore = 70,
  title = 'Quiz'
}) => {
  const [answers, setAnswers] = useState<{ [key: number]: number | number[] }>({});
  const [submitted, setSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);

  // Check if question is multiple-select based on question text
  const isMultipleSelect = (question: QuizQuestion) => {
    return question.question.toLowerCase().includes('select all that apply');
  };

  const handleAnswerChange = (questionIndex: number, answerIndex: number) => {
    setAnswers({
      ...answers,
      [questionIndex]: answerIndex
    });
  };

  const handleMultipleAnswerChange = (questionIndex: number, optionIndex: number, checked: boolean) => {
    const currentAnswers = (answers[questionIndex] as number[]) || [];
    let newAnswers: number[];
    
    if (checked) {
      newAnswers = [...currentAnswers, optionIndex].sort((a, b) => a - b);
    } else {
      newAnswers = currentAnswers.filter(a => a !== optionIndex);
    }
    
    setAnswers({
      ...answers,
      [questionIndex]: newAnswers
    });
  };

  // Parse correctAnswer - can be number, array, or comma-separated string
  const parseCorrectAnswer = (answer: number | number[] | string): number[] | number => {
    if (typeof answer === 'string') {
      // Parse comma-separated string to array
      return answer.split(',').map(n => parseInt(n.trim()));
    }
    return answer;
  };

  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort((x, y) => x - y);
    const sortedB = [...b].sort((x, y) => x - y);
    return sortedA.every((val, idx) => val === sortedB[idx]);
  };

  const calculateScore = () => {
    let correct = 0;
    questions.forEach((q, index) => {
      const userAnswer = answers[index];
      const correctAnswer = parseCorrectAnswer(q.correctAnswer);
      
      // Handle multiple-select questions (arrays)
      if (Array.isArray(correctAnswer)) {
        if (Array.isArray(userAnswer) && arraysEqual(userAnswer, correctAnswer)) {
          correct++;
        }
      } else {
        // Handle single-select questions (numbers)
        if (userAnswer === correctAnswer) {
          correct++;
        }
      }
    });
    return Math.round((correct / questions.length) * 100);
  };

  const handleSubmit = () => {
    const score = calculateScore();
    const passed = score >= passingScore;
    setSubmitted(true);
    setShowResults(true);
    onComplete(score, passed);
  };

  const isAnswerCorrect = (questionIndex: number) => {
    const userAnswer = answers[questionIndex];
    const correctAnswer = parseCorrectAnswer(questions[questionIndex].correctAnswer);
    
    if (Array.isArray(correctAnswer)) {
      return Array.isArray(userAnswer) && arraysEqual(userAnswer, correctAnswer);
    }
    return userAnswer === correctAnswer;
  };

  const allQuestionsAnswered = questions.every((_, index) => answers[index] !== undefined);

  if (!questions || questions.length === 0) {
    return (
      <Alert severity="info">
        No quiz questions available for this lesson.
      </Alert>
    );
  }

  const score = submitted ? calculateScore() : 0;
  const passed = score >= passingScore;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
        {title}
      </Typography>

      {submitted && (
        <Alert
          severity={passed ? 'success' : 'error'}
          sx={{ mb: 3 }}
          icon={passed ? <CheckCircleIcon /> : <CancelIcon />}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="h6">
                Score: {score}% {passed ? '(Passed)' : '(Failed)'}
              </Typography>
              <Typography variant="body2">
                {passed
                  ? `Congratulations! You passed with ${score}%. Passing score is ${passingScore}%.`
                  : `You scored ${score}%. You need ${passingScore}% to pass. Please review the material and try again.`}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setReviewMode(!reviewMode)}
              sx={{
                borderColor: passed ? '#4caf50' : '#f44336',
                color: passed ? '#4caf50' : '#f44336',
                '&:hover': {
                  borderColor: passed ? '#45a049' : '#d32f2f',
                  bgcolor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              {reviewMode ? 'Hide Results' : 'Review Results'}
            </Button>
          </Box>
        </Alert>
      )}

      {!submitted && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Typography variant="body2">
            <strong>Instructions:</strong> Answer all {questions.length} questions below. You need{' '}
            {passingScore}% to pass.
          </Typography>
          <LinearProgress
            variant="determinate"
            value={(Object.keys(answers).length / questions.length) * 100}
            sx={{ mt: 2 }}
          />
          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
            {Object.keys(answers).length} of {questions.length} questions answered
          </Typography>
        </Paper>
      )}

      {(!submitted || reviewMode) && questions.map((question, qIndex) => (
        <Card
          key={qIndex}
          sx={{
            mb: 3,
            border: submitted
              ? isAnswerCorrect(qIndex)
                ? '2px solid #4caf50'
                : '2px solid #f44336'
              : '1px solid #e0e0e0'
          }}
        >
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ color: '#003366' }}>
              Question {qIndex + 1}
            </Typography>
            <Typography variant="body1" paragraph>
              {question.question}
            </Typography>

            <FormControl component="fieldset" fullWidth disabled={submitted}>
              {isMultipleSelect(question) ? (
                // Multiple-select question (checkboxes)
                <FormGroup>
                  {question.options.map((option, oIndex) => {
                    const parsedAnswer = parseCorrectAnswer(question.correctAnswer);
                    const correctAnswers = Array.isArray(parsedAnswer) ? parsedAnswer : [];
                    const isCorrect = correctAnswers.includes(oIndex);
                    const userAnswers = (answers[qIndex] as number[]) || [];
                    const isSelected = userAnswers.includes(oIndex);

                    return (
                      <FormControlLabel
                        key={oIndex}
                        control={
                          <Checkbox
                            checked={isSelected}
                            onChange={(e) => handleMultipleAnswerChange(qIndex, oIndex, e.target.checked)}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography>{option}</Typography>
                            {submitted && isCorrect && (
                              <CheckCircleIcon sx={{ ml: 1, color: '#4caf50', fontSize: 20 }} />
                            )}
                            {submitted && isSelected && !isCorrect && (
                              <CancelIcon sx={{ ml: 1, color: '#f44336', fontSize: 20 }} />
                            )}
                          </Box>
                        }
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: submitted
                            ? isCorrect
                            ? '#e8f5e9'
                            : isSelected
                            ? '#ffebee'
                            : 'transparent'
                          : 'transparent',
                        '&:hover': {
                          bgcolor: submitted ? undefined : '#f5f5f5'
                        }
                      }}
                    />
                  );
                })}
                </FormGroup>
              ) : (
                // Single-select question (radio buttons)
                <RadioGroup
                  value={answers[qIndex] !== undefined ? answers[qIndex] : ''}
                  onChange={(e) => handleAnswerChange(qIndex, parseInt(e.target.value))}
                >
                  {question.options.map((option, oIndex) => {
                    const isCorrect = oIndex === question.correctAnswer;
                    const isSelected = answers[qIndex] === oIndex;

                    return (
                      <FormControlLabel
                        key={oIndex}
                        value={oIndex}
                        control={<Radio />}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography>{option}</Typography>
                            {submitted && isCorrect && (
                              <CheckCircleIcon sx={{ ml: 1, color: '#4caf50', fontSize: 20 }} />
                            )}
                            {submitted && isSelected && !isCorrect && (
                              <CancelIcon sx={{ ml: 1, color: '#f44336', fontSize: 20 }} />
                            )}
                          </Box>
                        }
                        sx={{
                          mb: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: submitted
                            ? isCorrect
                            ? '#e8f5e9'
                            : isSelected
                            ? '#ffebee'
                            : 'transparent'
                          : 'transparent',
                        '&:hover': {
                          bgcolor: submitted ? undefined : '#f5f5f5'
                        }
                      }}
                      />
                    );
                  })}
                </RadioGroup>
              )}
            </FormControl>

            {submitted && question.explanation && (
              <>
                <Divider sx={{ my: 2 }} />
                <Alert severity="info" icon={false}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Explanation:
                  </Typography>
                  <Typography variant="body2">{question.explanation}</Typography>
                </Alert>
              </>
            )}
          </CardContent>
        </Card>
      ))}

      {!submitted && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="contained"
            size="large"
            onClick={handleSubmit}
            disabled={!allQuestionsAnswered}
            sx={{
              bgcolor: '#003366',
              '&:hover': { bgcolor: '#002244' },
              px: 4,
              py: 1.5
            }}
          >
            Submit Quiz
          </Button>
        </Box>
      )}

      {submitted && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              setAnswers({});
              setSubmitted(false);
              setShowResults(false);
            }}
            sx={{
              borderColor: '#003366',
              color: '#003366',
              '&:hover': { borderColor: '#002244', bgcolor: '#f5f5f5' },
              px: 4,
              py: 1.5
            }}
          >
            {passed ? 'Retake to Improve Score' : 'Retake Quiz'}
          </Button>
          {passed && (
            <Button
              variant="contained"
              size="large"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#45a049' },
                px: 4,
                py: 1.5
              }}
            >
              Continue to Next Lesson
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default QuizEngine;
